import type { SupabaseClient } from '@supabase/supabase-js';

const BUCKET = 'profile-avatars';
const MAX_INPUT_BYTES = 5 * 1024 * 1024;

/** Resize in-browser and encode as JPEG for consistent path and smaller uploads. */
export async function resizeImageFileToJpeg(file: File, maxEdge = 512): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Image must be 5 MB or smaller.');
  }
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process image.');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Could not encode image'))),
        'image/jpeg',
        0.88
      );
    });
    return blob;
  } finally {
    bitmap.close();
  }
}

export function profileAvatarStoragePath(userId: string): string {
  return `${userId}/avatar.jpg`;
}

export async function uploadProfileAvatar(
  client: SupabaseClient,
  userId: string,
  imageBlob: Blob
): Promise<{ publicUrl: string } | { error: string }> {
  const path = profileAvatarStoragePath(userId);
  const { error: uploadError } = await client.storage.from(BUCKET).upload(path, imageBlob, {
    upsert: true,
    contentType: 'image/jpeg',
    cacheControl: '3600',
  });
  if (uploadError) {
    return { error: uploadError.message };
  }
  const { data } = client.storage.from(BUCKET).getPublicUrl(path);
  return { publicUrl: data.publicUrl };
}

export async function removeProfileAvatarObject(client: SupabaseClient, userId: string): Promise<void> {
  const path = profileAvatarStoragePath(userId);
  await client.storage.from(BUCKET).remove([path]);
}
